import { callable } from '../backend/callables';
import type { BrowserGrantPayload } from './pairing';
import { generateTransportKey, openBrowserGrant, renderPairingLink } from './pairing';

/**
 * Driving the rendezvous from the joiner's side: ask for a session, show the
 * link, wait for the phone to answer, open what it left.
 *
 * All three callables are auth-only — the joiner has no key yet, so it has
 * nothing to present but its identity. Both ends authenticate as the same
 * uid, which is why a photographed QR is not enough on its own: reading the
 * payload also takes a signed-in session for that account.
 */

const POLL_INTERVAL_MS = 2000;

export interface PairingSession {
  sessionId: string;
  transportKey: Uint8Array;
  link: string;
  expiresAtMs: number;
}

export async function startPairing(idToken: string, label: string): Promise<PairingSession> {
  const transportKey = generateTransportKey();
  const { sessionId, expiresAtMs } = await callable<{ sessionId: string; expiresAtMs: number }>(
    'startVaultPairing',
    { kind: 'browser', label },
    { idToken },
  );
  return { sessionId, transportKey, link: renderPairingLink(sessionId, transportKey), expiresAtMs };
}

export type PairingOutcome =
  | { state: 'granted'; grant: BrowserGrantPayload }
  | { state: 'expired' }
  | { state: 'undecipherable' };

/**
 * Polls until the custodian answers or the session dies. An answered session
 * is deleted on success so the ciphertext stops existing sooner than expiry
 * would remove it.
 */
export async function awaitGrant(
  idToken: string,
  session: PairingSession,
  signal: AbortSignal,
): Promise<PairingOutcome> {
  while (!signal.aborted) {
    if (Date.now() >= session.expiresAtMs) return { state: 'expired' };

    let answered: { sealedPayload: string | null } | null = null;
    try {
      answered = await callable<{ sealedPayload: string | null }>(
        'getVaultPairing',
        { sessionId: session.sessionId },
        { idToken, signal },
      );
    } catch {
      // A session that expired between the check above and this call answers
      // deadline-exceeded; anything else is worth one more poll rather than
      // dropping a QR the user is still holding a phone up to.
      answered = null;
    }

    if (answered?.sealedPayload) {
      const grant = openBrowserGrant(session.transportKey, session.sessionId, answered.sealedPayload);
      await callable('finishVaultPairing', { sessionId: session.sessionId }, { idToken }).catch(() => {});
      return grant ? { state: 'granted', grant } : { state: 'undecipherable' };
    }

    await sleep(POLL_INTERVAL_MS, signal);
  }
  return { state: 'expired' };
}

/** A readable label for the custodian's approval prompt. Deliberately coarse
 *  — it is a recognition aid on the phone, not a fingerprint. */
export function describeThisBrowser(): string {
  const ua = navigator.userAgent;
  const browser = /Firefox\//.test(ua)
    ? 'Firefox'
    : /Edg\//.test(ua)
      ? 'Edge'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'A browser';
  const os = /Mac OS X/.test(ua)
    ? 'macOS'
    : /Windows/.test(ua)
      ? 'Windows'
      : /Linux/.test(ua)
        ? 'Linux'
        : null;
  return os ? `${browser} on ${os}` : browser;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}
